import type { ReactNode } from 'react';

import { Input } from '@web/ui/core/atoms';

import { FieldHint } from './field_hint';
import { FieldLabel } from './field_label';

type FormTextFieldProps = {
  field: {
    name: string;
    state: { value: unknown; meta: { errors: unknown[]; isTouched: boolean } };
    handleBlur: () => void;
    handleChange: (value: string) => void;
  };
  label: ReactNode;
  required?: boolean;
  labelEndAdornment?: ReactNode;
  hint?: ReactNode;
  /** Optional value transform run before `field.handleChange` (e.g. uppercase). */
  transformValue?: (raw: string) => string;
  onAfterChange?: (next: string) => void;
  /** Override default `isTouched && hasError` gating for error display. */
  showErrorWhen?: boolean;

  placeholder?: string;
  autoFocus?: boolean;
  autoComplete?: string;
  spellCheck?: boolean;
  disabled?: boolean;
  readOnly?: boolean;
  /** Render input text in the mono font (codes, identifiers). */
  mono?: boolean;
  /** Icon rendered inside the field, before the input. */
  leadingIcon?: ReactNode;
  /** Fixed prefix block with a right divider (e.g. "PROJECT."). */
  startAdornment?: ReactNode;
};

/*
 * TanStack Form-bound text input. Wraps the DS <Input> primitive and owns
 * label/error/hint scaffolding. Field value/touched/errors drive the
 * rendered state; the parent never reaches into the DOM.
 */
export function FormTextField(props: Readonly<FormTextFieldProps>): React.JSX.Element {
  const {
    field,
    label,
    required,
    labelEndAdornment,
    hint,
    transformValue,
    onAfterChange,
    showErrorWhen,
    placeholder,
    autoFocus,
    autoComplete,
    spellCheck,
    disabled,
    readOnly,
    mono,
    leadingIcon,
    startAdornment,
  } = props;

  const firstError = field.state.meta.errors[0];
  const rawError = typeof firstError === 'string' ? firstError : undefined;
  const isTouched = field.state.meta.isTouched;
  const showError = showErrorWhen ?? (isTouched && !!rawError);
  const errorText = showError ? rawError : undefined;

  const handleChange = (raw: string): void => {
    const next = transformValue ? transformValue(raw) : raw;
    field.handleChange(next);
    onAfterChange?.(next);
  };

  return (
    <div className="mb-4.5 flex flex-col gap-1">
      <FieldLabel htmlFor={field.name} required={required} endAdornment={labelEndAdornment}>
        {label}
      </FieldLabel>
      <Input
        id={field.name}
        name={field.name}
        value={typeof field.state.value === 'string' ? field.state.value : ''}
        onBlur={field.handleBlur}
        onChange={(e) => {
          handleChange(e.target.value);
        }}
        placeholder={placeholder}
        autoFocus={autoFocus}
        autoComplete={autoComplete}
        spellCheck={spellCheck}
        disabled={disabled}
        readOnly={readOnly}
        aria-invalid={showError || undefined}
        leadingIcon={leadingIcon}
        startAdornment={startAdornment}
        mono={mono}
      />
      <FieldHint hint={hint} error={errorText} />
    </div>
  );
}
